import { defineComponent as _defineComponent } from 'vue'
import { useSlots, PropType, computed, ref } from 'vue';
import { useManagedListBlock } from 'ah-common-lib/src/listing/useManagedListBlock';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import {
  UseManagedListBlockEmits,
  defineUseManagedListBlockProps,
} from 'ah-common-lib/src/listing/useManagedListBlockInterfaces';
import { ListingTriggers } from 'ah-common-lib/src/common/components/listing/Listing.vue';
import ListPageCounter from './ListPageCounter.vue';
import ListPagination from './ListPagination.vue';
import MaterialIcon from '../common/MaterialIcon.vue';
import { isEmpty, isEqual } from 'lodash';
import { VButton } from 'ah-common-lib/src/common/components';
import ListDownloadButton from 'ah-common-lib/src/common/components/listing/ListDownloadButton.vue';

interface AHPanelListBlockEmits extends UseManagedListBlockEmits {}


export default /*#__PURE__*/_defineComponent({
  __name: 'BDListBlock',
  props: {
  ...defineUseManagedListBlockProps(),

  showExport: { type: [Boolean, String], default: false },

  showColConfigurator: { type: [Boolean, String], default: false },

  showSizeSelector: { type: [Boolean, String], default: true },

  downloadFormatOptions: {
    type: Array as PropType<{ value: string; text: string }[]>,
    default: () => [
      { value: 'PDF', text: 'PDF' },
      { value: 'XLSX', text: 'Excel' },
      { value: 'CSV', text: 'CSV' },
    ],
  },

  pageOptions: {
    type: Array as PropType<{ label: string; value: number }[]>,
    default: () => [
      { label: '10', value: 10 },
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
    ],
  },
},
  setup(__props, { expose, emit }: { emit: ({}), expose: any, slots: any, attrs: any }) {

const props = __props;

const slots = useSlots();

/**
 * Base List block component
 *
 * Meant for usage alongside a <Listing> and a <fooFilter> components, controlling filter flow between both
 * All data is synchronized via slot scopes to Listing and Filter components
 *
 * This component does NOT save filter/pagination data. Any query-string-based data saving should happen on the child components
 * (or, as is common, on the Filter components, which MAY ALSO save pagination data)
 */





const requestManager = useRequestManager().manager;

const filtersShown = ref(false);

const isSearchBannerShown = computed(() => managedListBlock.refs.filter.value?.hasOwnProperty('query'));

const managedListBlock = useManagedListBlock({
  reqManager: requestManager,
  props,
  emit,
});

const listingTriggers = ref<ListingTriggers<any>>();

const filterSlotScope = computed(() => managedListBlock.filterSlotScope.value);

/**
 * Slot scope for the list slot. All necessary hooks are exposed, grouped into `listeners` and `props` for a less verbose integration
 *
 * Listeners for filter and sortAndPageParams for listings are SYNC by default (as opposed to the filter listeners, which update on a debounce)
 */
const listSlotScope = computed(() => {
  return {
    ...managedListBlock.listSlotScope.value,
    listeners: {
      ...managedListBlock.listSlotScope.value.listeners,
      ['update:triggers']: onListTriggersUpdate,
    },
    props: managedListBlock.listSlotScope.value.props,
    onListTriggersUpdate: onListTriggersUpdate,
    downloadData: downloadData,
    loadData: loadData,
    hasCustomFilters,
  };
});

function onListTriggersUpdate(triggers: ListingTriggers<any>) {
  listingTriggers.value = triggers;
}

function loadData() {
  listingTriggers.value?.loadData();
}

function downloadData(type: string) {
  listingTriggers.value?.downloadData(type);
}

const hasCustomFilters = computed(() => {
  return (
    managedListBlock.refs.filter.value &&
    !isEmpty(managedListBlock.refs.filter.value) &&
    !isEqual(managedListBlock.refs.commonFilter.value, managedListBlock.refs.filter.value)
  );
});

const footerSlotScope = computed(() => {
  return {
    setPage: managedListBlock.setPage,
    setPageSize: managedListBlock.setPageSize,
    data: managedListBlock.refs.data,
    dataDownloadState: managedListBlock.refs.dataDownloadState,
    downloadData: downloadData,
    loadData: loadData,
  };
});

const dataDownloadState = computed(() => managedListBlock.refs.dataDownloadState.value);

expose({
  managedListBlock,
});

return { __sfc: true,slots, props, emit, requestManager, filtersShown, isSearchBannerShown, managedListBlock, listingTriggers, filterSlotScope, listSlotScope, onListTriggersUpdate, loadData, downloadData, hasCustomFilters, footerSlotScope, dataDownloadState, ListPageCounter, ListPagination, MaterialIcon, VButton, ListDownloadButton }
}

})